"use client";

import React, { useState, useMemo } from "react";
import { useParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import {
  useFetchNewsArticles,
  useFetchNewsArticlesCount,
} from "@/apis/queryFunctions/news";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import AppBreadcrumbs from "@/components/Common/AppBreadcrumbs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useHeader } from "@/context/HeaderLayoutContext";
import { useNewsBanner } from "@/hooks/useNewsBanner";
import { useTranslatedText } from "@/hooks/useProductTranslation";
import styles from "./categoryNews.module.css";

const LIMIT = 12;

function NewsCardTitle({ title, locale }: { title: string; locale: string }) {
  const displayTitle = useTranslatedText(title, locale);
  return <>{displayTitle}</>;
}

export default function TeaNewsPage() {
  const params = useParams();
  const locale = (params?.locale as string) || "vi";
  const [page, setPage] = useState(1);
  const [jumpPage, setJumpPage] = useState("");

  const bannerImg = useNewsBanner(
    "banner-news-tra",
    "/images/new_banner.png",
    locale
  );

  const { setHeaderStyle } = useHeader();

  React.useEffect(() => {
    setHeaderStyle({ transparent: true });
    return () => setHeaderStyle({ transparent: false });
  }, [setHeaderStyle]);

  const breadcrumbs = [
    { title: locale === "vi" ? "Trang chủ" : "Home", href: `/${locale}` },
    {
      title: locale === "vi" ? "Tin tức Việt Thiên" : "Viet Thien News",
      href: `/${locale}/tin-tuc`,
    },
    { title: locale === "vi" ? "Tin tức trà" : "Tea News", href: "#" },
  ];

  const options = useMemo(
    () => ({
      page,
      limit: LIMIT,
      condition: {
        ngon_ngu: locale,
        loai_tin_tuc: { $in: ["Tin tức trà", "Trà", "Tea"] },
      },
    }),
    [page, locale]
  );

  const { data: news, isLoading } = useFetchNewsArticles(options);
  const { data: newsCount } = useFetchNewsArticlesCount(options);

  const totalPages = Math.ceil((newsCount || 0) / LIMIT);

  const handleJump = () => {
    const pageNum = parseInt(jumpPage);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
      setJumpPage("");
    }
  };

  const getPageRange = () => {
    const range = [];
    const delta = 2;
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= page - delta && i <= page + delta)
      ) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }
    return range;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className={styles.pagination}>
        <button
          className={styles.pageBtn}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          <ChevronLeft size={18} />
        </button>

        {getPageRange().map((p, i) =>
          p === "..." ? (
            <span key={`dots-${i}`} className={styles.ellipsis}>
              ...
            </span>
          ) : (
            <button
              key={`page-${p}`}
              className={`${styles.pageBtn} ${page === p ? styles.activePage : ""}`}
              onClick={() => setPage(Number(p))}
            >
              {p}
            </button>
          )
        )}

        <button
          className={styles.pageBtn}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
        >
          <ChevronRight size={18} />
        </button>

        <div className={styles.jumpWrapper}>
          <input
            type="number"
            className={styles.jumpInput}
            placeholder={locale === "vi" ? "Trang..." : "Page..."}
            value={jumpPage}
            onChange={(e) => setJumpPage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleJump()}
          />
          <button className={styles.jumpBtn} onClick={handleJump}>
            <ChevronRight size={18} />
          </button>
        </div>

        <div className={styles.pageInfo}>
          {page} / {totalPages}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      {/* Hero Banner */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <Image
            src={bannerImg}
            alt={locale === "vi" ? "Tin tức trà" : "Tea News"}
            fill
            priority
            sizes="100vw"
            className={styles.bannerBgImage}
          />
          <div className={styles.heroMainContent}>
            <h1 className={styles.heroTitle}>
              {locale === "vi" ? "Tin tức trà" : "Tea News"}
            </h1>
          </div>
        </div>
      </section>

      <div className={styles.container}>
        <div className={styles.breadcrumbsWrapper}>
          <AppBreadcrumbs items={breadcrumbs} isLoading={false} />
        </div>

        <div className={styles.grid}>
          {isLoading ? (
            [...Array(LIMIT)].map((_, i) => (
              <div
                key={i}
                className={`${styles.card} ${styles.skeleton}`}
                style={{ height: "300px" }}
              />
            ))
          ) : news && news.length > 0 ? (
            news.map((item: any) => (
              <Link
                key={item._id}
                href={`/${locale}/tin-tuc/${item.slug}`}
                className={styles.card}
              >
                <div className={styles.imgWrapper}>
                  <Image
                    src={appendImageUrlFromAPI({
                      src: item.picture,
                      size: "m",
                    })}
                    alt={item.title}
                    fill
                    style={{ objectFit: "cover" }}
                  />
                </div>
                <div className={styles.content}>
                  <h3 className={styles.title}>
                    <NewsCardTitle title={item.title} locale={locale} />
                  </h3>
                  <div className={styles.date}>
                    {new Date(item.date_updated).toLocaleDateString(locale)}
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                padding: "100px 0",
              }}
            >
              {locale === "vi"
                ? "Không có tin tức nào."
                : "No news articles found."}
            </div>
          )}
        </div>

        {renderPagination()}
      </div>
    </div>
  );
}
